import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import TabNavigation from '../../components/ui/TabNavigation';
import BookingForm from './components/BookingForm';
import CustomerDetailsPanel from './components/CustomerDetailsPanel';
import AvailabilityChecker from './components/AvailabilityChecker';
import BookingsList from './components/BookingsList';

const BookingManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [customers, setCustomers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch customers from Supabase
  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase?.from('customers')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    }
  };

  // Fetch appointments from Supabase with retry logic for schema cache
  const fetchAppointments = async (retryCount = 0) => {
    try {
      const { data, error } = await supabase?.from('bookings')?.select('*')?.order('date', { ascending: true });

      if (error) {
        // Handle schema cache error with retry
        if (error?.code === 'PGRST205' && retryCount < 3) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          console.log(`Schema cache not ready, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchAppointments(retryCount + 1);
        }
        throw error;
      }
      setAppointments(data || []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      if (err?.code === 'PGRST205') {
        setError('Database schema is updating. Please refresh the page in a moment.');
      } else {
        setError('Failed to load appointments');
      }
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchAppointments()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (location?.state) {
      const { booking, date, time } = location?.state;
      if (booking) {
        setCurrentBooking(booking);
        setSelectedCustomerId(booking?.customer_id || booking?.customerId);
      }
      if (date) {
        setSelectedDate(date);
      }
      if (time) {
        setSelectedTime(time);
      }
    }
  }, [location?.state]);

  const handleSaveBooking = async (bookingData) => {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        customer_id: bookingData?.customerId,
        service_id: bookingData?.serviceId || null,
        date: bookingData?.date,
        time: bookingData?.time,
        duration: parseInt(bookingData?.duration),
        service: bookingData?.service,
        notes: bookingData?.notes || '',
        status: bookingData?.status || 'scheduled',
        customer_name: bookingData?.customerName,
        customer_email: bookingData?.customerEmail,
        customer_phone: bookingData?.customerPhone
      };

      if (currentBooking) {
        // Update existing booking
        const { data, error } = await supabase?.from('bookings')?.update(dbData)?.eq('id', currentBooking?.id)?.select()?.single();

        if (error) throw error;
        setAppointments((prev) =>
          prev?.map((apt) => apt?.id === currentBooking?.id ? data : apt)
        );
      } else {
        // Create new booking
        const { data, error } = await supabase?.from('bookings')?.insert([dbData])?.select()?.single();

        if (error) throw error;
        setAppointments((prev) => [...prev, data]);
      }

      navigate('/calendar-view', { state: { refresh: true } });
    } catch (err) {
      console.error('Error saving booking:', err);
      throw new Error('Failed to save booking. Please try again.');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }

    try {
      const { error } = await supabase?.from('bookings')?.delete()?.eq('id', bookingId);

      if (error) throw error;

      setAppointments((prev) => prev?.filter((apt) => apt?.id !== bookingId));
      
      // Clear current booking if it's the one being deleted
      if (currentBooking?.id === bookingId) {
        setCurrentBooking(null);
      }
    } catch (err) {
      console.error('Error deleting booking:', err);
      alert('Failed to delete booking. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate('/calendar-view');
  };

  const handleCreateCustomer = async (newCustomer) => {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        name: newCustomer?.name,
        email: newCustomer?.email,
        phone: newCustomer?.phone,
        service_preference: newCustomer?.servicePreference || '',
        notes: newCustomer?.notes || ''
      };

      const { data, error } = await supabase?.from('customers')?.insert([dbData])?.select()?.single();

      if (error) throw error;

      setCustomers((prev) => [...prev, data]);
      setSelectedCustomerId(data?.id);
      
      // Return the created customer with database-generated ID
      return data;
    } catch (err) {
      console.error('Error creating customer:', err);
      throw new Error('Failed to create customer. Please try again.');
    }
  };

  const handleFormChange = (data) => {
    setFormData(data);
    if (data?.customerId) {
      setSelectedCustomerId(data?.customerId);
    }
  };

  const selectedCustomer = customers?.find(c => c?.id === selectedCustomerId);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading booking data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-error text-lg mb-2">Error Loading Data</p>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => window.location?.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TabNavigation />
      <div className="container mx-auto px-4 py-6 md:py-8 lg:py-10">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
            Booking Management
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Create and manage appointments for your customers
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2 space-y-6">
            <BookingForm
              booking={currentBooking}
              customers={customers}
              onSave={handleSaveBooking}
              onCancel={handleCancel}
              onDelete={handleDeleteBooking}
              onCreateCustomer={handleCreateCustomer}
              selectedDate={selectedDate}
              selectedTime={selectedTime} />

            <AvailabilityChecker
              selectedDate={formData?.date}
              selectedTime={formData?.time}
              duration={formData?.duration}
              appointments={appointments}
              currentBookingId={currentBooking?.id}
            />
          </div>

          <div className="lg:col-span-1">
            <CustomerDetailsPanel
              customer={selectedCustomer}
              appointments={appointments?.filter(apt => apt?.customer_id === selectedCustomerId || apt?.customerId === selectedCustomerId)}
            />
          </div>
        </div>

        {/* Bookings List Section */}
        <div className="mt-8">
          <BookingsList
            bookings={appointments}
            onEdit={(booking) => {
              setCurrentBooking(booking);
              setSelectedCustomerId(booking?.customer_id);
              setSelectedDate(booking?.date);
              setSelectedTime(booking?.time);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onDelete={handleDeleteBooking}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default BookingManagement;